import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Hero } from './Hero'
import { Differentiators } from './Differentiators'
import { ICPSection } from './ICPSection'
import { HowItWorks } from './HowItWorks'
import { PaymentModes } from './PaymentModes'
import { PricingSection } from './PricingSection'
import { FAQ } from './FAQ'
import { Footer } from './Footer'
import { useSEO, getSEOConfig, type SupportedLanguage } from '@/lib/seo'
import { getCachedGeoLocale } from '@/lib/geo-locale'

export type Language = 'pt' | 'en' | 'es'
export type Currency = 'BRL' | 'USD' | 'EUR'

const FAQ_ITEMS = {
  pt: [
    { question: 'PinDay é agenda de horários ou reserva de datas?', answer: 'Depende da configuração do serviço: PinDay confirma a reserva com pagamento, regras e o bloqueio correto no calendário.' },
    { question: 'Qual a diferença entre one-off e recorrente?', answer: 'One-off é cobrança única com sinal e saldo posterior. Recorrente usa checkout de assinatura via Stripe e está disponível em Pro e Studio.' },
    { question: 'Como funciona a fila de espera?', answer: 'Em Pro e Studio, quando uma data é liberada no PinDay, a próxima pessoa da fila recebe uma oferta exclusiva de 24 horas por e-mail.' },
  ],
  en: [
    { question: 'Is PinDay for time slots or date reservations?', answer: 'It depends on the service configuration: PinDay confirms the booking with payment, rules, and the right calendar block.' },
    { question: 'What is the difference between one-off and recurring?', answer: 'One-off is a single charge with deposit and optional balance later. Recurring uses Stripe subscription checkout and is available on Pro and Studio.' },
    { question: 'How does the waitlist work?', answer: 'On Pro and Studio, when a date becomes available inside PinDay, the next person in line receives an exclusive 24-hour offer by email.' },
  ],
  es: [
    { question: '¿PinDay es para horarios o para reservar fechas?', answer: 'Depende de la configuración del servicio: PinDay confirma la reserva con pago, reglas y el bloqueo correcto en el calendario.' },
    { question: '¿Cuál es la diferencia entre one-off y recurrente?', answer: 'One-off es un cobro único con seña y saldo posterior. Recurrente usa checkout de suscripción con Stripe y está disponible en Pro y Studio.' },
    { question: '¿Cómo funciona la lista de espera?', answer: 'En Pro y Studio, cuando una fecha se libera dentro de PinDay, la siguiente persona de la fila recibe una oferta exclusiva de 24 horas por email.' },
  ],
}

export function LandingPage() {
  const { locale } = useParams<{ locale: string }>()
  const [language, setLanguage] = useState<Language>('pt')
  const [currency, setCurrency] = useState<Currency>(
    () => getCachedGeoLocale()?.currency ?? 'BRL'
  )

  useEffect(() => {
    const geoCurrency: Currency = getCachedGeoLocale()?.currency ?? (locale === 'pt' ? 'BRL' : 'USD')

    if (locale === 'en') {
      setLanguage('en')
    } else if (locale === 'es') {
      setLanguage('es')
    } else {
      setLanguage('pt')
    }
    setCurrency(geoCurrency)
  }, [locale])

  const seoConfig = getSEOConfig('home', language as SupportedLanguage)

  useSEO({
    title: seoConfig?.title || 'PinDay - Reserve datas com cobrança one-off ou recorrente',
    description: seoConfig?.description || 'Envie a proposta, receba o pagamento e bloqueie a data com regras claras de cancelamento, reembolso e fila de espera Pro+.',
    keywords: seoConfig?.keywords || ['reserva de datas', 'one-off', 'recorrente', 'stripe', 'fila de espera'],
    language: language as SupportedLanguage,
    path: `/${language}/`,
    faq: FAQ_ITEMS[language],
  })

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#f6ebdd_0%,#fffaf3_40%,#ffffff_100%)] text-foreground">
      <Hero
        language={language}
        currency={currency}
        onLanguageChange={setLanguage}
        onCurrencyChange={setCurrency}
      />
      <Differentiators language={language} />
      <HowItWorks language={language} />
      <PaymentModes language={language} />
      <ICPSection language={language} />
      <PricingSection language={language} currency={currency} />
      <FAQ language={language} />
      <Footer language={language} />
    </div>
  )
}
